import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader, classes } from '../../components/ui.tsx';
import { useDocumentTitle } from '../../hooks/index.ts';

export interface SettingsLeafTab {
  to: string;
  label: string;
  /** Whether this child is the one on screen; the page decides, since a hub's bare path is its first child. */
  current: boolean;
}

/**
 * The frame every Settings child page shares: the way back to Settings, the
 * page heading, and — for a hub — links to its child pages.
 *
 * The tabs are links rather than a tablist: each child is its own route, so
 * it can be bookmarked, opened in a new tab, and reached with Back. Only the
 * child on screen is mounted, so only its queries run.
 */
export function SettingsLeaf({
  title,
  description,
  tabs,
  tabsLabel,
  anchorId,
  children,
}: {
  title: string;
  description?: ReactNode;
  tabs?: SettingsLeafTab[];
  /** Accessible name for the tab links; required with `tabs`. */
  tabsLabel?: string;
  /**
   * The id the card's wrapper had on /settings (`settings-team`, …). Kept on
   * the page so anything that finds the card by it — automation, a support
   * script — still does after the old hash forwards here.
   */
  anchorId?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  useDocumentTitle(title);
  return (
    <>
      <p className="mb-2 text-sm">
        <Link className="font-semibold text-[color:var(--color-brand)] hover:underline" to="/settings">
          {t('settings.leaf.back')}
        </Link>
      </p>
      <PageHeader title={title} description={description} />
      {tabs && tabs.length > 0 ? (
        <nav aria-label={tabsLabel} className="mb-6 border-b border-[color:var(--color-line)]">
          <ul className="scrollbar-none -mb-px flex gap-1 overflow-x-auto">
            {tabs.map((tab) => (
              <li key={tab.to} className="shrink-0">
                <Link
                  to={tab.to}
                  aria-current={tab.current ? 'page' : undefined}
                  className={classes(
                    'inline-block border-b-2 px-3 py-2 text-sm whitespace-nowrap',
                    tab.current
                      ? 'border-[color:var(--color-brand)] font-semibold text-[color:var(--color-brand-strong)]'
                      : 'border-transparent text-[color:var(--color-muted)] hover:text-[color:var(--color-ink)]',
                  )}
                >
                  {tab.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      ) : null}
      {anchorId ? <div id={anchorId}>{children}</div> : children}
    </>
  );
}
